import type { TravelPosture } from '@opensentry/shared';
import { useMutation } from '@tanstack/react-query';
import { Copy, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { CategoryIcon } from '../components/CategoryIcon.js';
import { EventDrawer } from '../components/EventDrawer.js';
import { SeverityPill } from '../components/SeverityPill.js';
import { Button } from '../components/ui/Button.js';
import { api } from '../lib/api.js';
import { timeAgo } from '../lib/format.js';

const RADII = [5, 10, 25, 50, 100, 200];

const POSTURE_COLOR: Record<TravelPosture, string> = {
  Routine: 'var(--color-sev-low)',
  Heightened: 'var(--color-sev-medium)',
  Restricted: 'var(--color-sev-high)',
  Avoid: 'var(--color-sev-critical)',
};

export function Lookup() {
  const [query, setQuery] = useState('');
  const [radius, setRadius] = useState(25);
  const [copied, setCopied] = useState(false);
  const [eventId, setEventId] = useState<number | null>(null);

  const lookup = useMutation({
    mutationFn: () => api.lookup({ address: query, radiusKm: radius }),
  });

  const brief = useMutation({
    mutationFn: () => api.travelBrief({ address: query, radiusKm: radius }),
  });

  // One action: the threat scan and the AI briefing run together.
  const assess = () => {
    if (!query.trim()) return;
    lookup.mutate();
    brief.mutate();
  };
  const pending = lookup.isPending || brief.isPending;

  const summary = lookup.data
    ? [
        `**Threat summary — ${lookup.data.query.label ?? query}** (${radius}km radius)`,
        '',
        ...lookup.data.events.map(
          (e) =>
            `- [${e.event.severity.toUpperCase()}] ${e.event.title} — ${e.event.source}, ${e.distanceKm.toFixed(0)}km, ${timeAgo(e.event.occurredAt)}`,
        ),
        lookup.data.events.length === 0 ? '- No active threats in range.' : '',
      ].join('\n')
    : '';

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6">
      <h1 className="text-lg font-semibold">Travel Location Advisories</h1>
      <p className="text-xs text-[var(--color-muted)]">
        Enter a city or <code className="mono">lat,lng</code> (e.g. "Bogotá"). One assessment
        returns the AI travel briefing and the full list of nearby threats.
      </p>
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          assess();
        }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Address, city, or coordinates"
          className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] px-3 py-2 text-sm outline-none"
        />
        <select
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] px-2 text-sm"
        >
          {RADII.map((r) => (
            <option key={r} value={r}>
              {r} km
            </option>
          ))}
        </select>
        <Button variant="primary" type="submit" disabled={pending || !query.trim()}>
          <Sparkles size={14} /> {pending ? 'Assessing…' : 'Assess location'}
        </Button>
      </form>

      {brief.data && (
        <div className="space-y-3 rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-4">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-[var(--color-sev-high)]" />
            <h2 className="text-sm font-semibold">
              Travel briefing — {brief.data.destination.label}
            </h2>
            <span
              className="ml-auto rounded px-2 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: POSTURE_COLOR[brief.data.posture], color: '#0b0b0c' }}
            >
              {brief.data.posture}
            </span>
          </div>
          <p className="text-sm">{brief.data.summary}</p>
          {brief.data.activeThreats.length > 0 && (
            <div>
              <h3 className="mb-1 text-xs font-semibold text-[var(--color-muted)]">
                Active threats
              </h3>
              <ul className="space-y-1 text-xs">
                {brief.data.activeThreats.map((t) => (
                  <li key={t}>• {t}</li>
                ))}
              </ul>
            </div>
          )}
          {brief.data.recentContext.length > 0 && (
            <div>
              <h3 className="mb-1 text-xs font-semibold text-[var(--color-muted)]">
                Recent context
              </h3>
              <ul className="space-y-1 text-xs text-[var(--color-muted)]">
                {brief.data.recentContext.map((t) => (
                  <li key={t}>• {t}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mono text-[11px] text-[var(--color-muted)]">
            {brief.data.sources.length} source(s) · {brief.data.meta.model}
            {brief.data.meta.costUsd != null ? ` · $${brief.data.meta.costUsd.toFixed(5)}` : ''}
            {brief.data.meta.degraded ? ' · rule-based (AI unavailable)' : ''}
          </div>
        </div>
      )}

      {lookup.isError && (
        <p className="text-sm text-[var(--color-sev-critical)]">
          Could not geocode or look up that location.
        </p>
      )}

      {lookup.data && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              {lookup.data.events.length} threats within {radius} km
            </h2>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(summary);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              <Copy size={14} /> {copied ? 'Copied' : 'Copy summary'}
            </Button>
          </div>
          <ul className="space-y-2">
            {lookup.data.events.map(({ event, distanceKm }) => (
              <li key={event.id}>
                <button
                  type="button"
                  onClick={() => setEventId(event.id)}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3 text-left hover:border-[var(--color-muted)]"
                >
                  <div className="flex items-center gap-2">
                    <CategoryIcon category={event.category} />
                    <SeverityPill severity={event.severity} />
                    <span className="ml-auto mono text-[11px] text-[var(--color-muted)]">
                      {distanceKm.toFixed(0)} km · {timeAgo(event.occurredAt)}
                    </span>
                  </div>
                  <div className="mt-1 text-sm">{event.title}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <EventDrawer eventId={eventId} onClose={() => setEventId(null)} />
    </div>
  );
}
